// Define interfaces for the formatted data
export interface Variable {
    name: string;
    definition: string;
}

export interface Parameter {
    name: string;
    definition: string;
}

export interface Constraint {
    title: string;
    description: string;
    formula: string;
}

export interface ObjectiveFunction {
    title: string;
    description: string;
    formula: string;
}

export interface FormattedFormulation {
    title: string;
    description: string;
    variables: Variable[];
    parameters: Parameter[];
    constraints: Constraint[];
    objective: ObjectiveFunction;
    jsonData: string;
    latexData: string;
}

export const formatFormulation = (formulation: string): FormattedFormulation | null => {
    if (!formulation) return null;

    try {
        // Split the formulation into major sections based on ## headings
        const sections = formulation.split(/(?=## )/);

        // Extract title and description (first section)
        const titleSection = sections[0].split('\n\n');
        const title = titleSection[0].replace('# ', '').trim();
        const description = titleSection.slice(1).join('\n\n').trim();

        // Process Decision Variables section
        const variablesSection = sections.find(section => section.startsWith('## Decision Variables'));
        let variables: Variable[] = [];
        if (variablesSection) {
            const variablesLines = variablesSection.split('\n- ').slice(1);
            variables = variablesLines.map(line => {
                const match = line.match(/\\?\(\s*(.*?)\s*\\?\):\s*(.*)/);
                return match ? {
                    name: match[1].trim(),
                    definition: match[2].trim()
                } : null;
            }).filter(Boolean) as Variable[];
        }

        // Process Parameters section
        const parametersSection = sections.find(section => section.startsWith('## Parameters'));
        let parameters: Parameter[] = [];
        if (parametersSection) {
            const parametersLines = parametersSection.split('\n- ').slice(1);
            parameters = parametersLines.map(line => {
                const match = line.match(/\\?\(\s*(.*?)\s*\\?\):\s*(.*)/);
                return match ? {
                    name: match[1].trim(),
                    definition: match[2].trim()
                } : null;
            }).filter(Boolean) as Parameter[];
        }

        // Process Constraints section
        const constraintsSection = sections.find(section => section.startsWith('## Constraints'));
        let constraints: Constraint[] = [];
        if (constraintsSection) {
            // Split by ### to get individual constraint sections
            const constraintSubsections = constraintsSection.split(/(?=### )/);

            // First part contains the general constraints title/description
            const constraintsTitle = constraintSubsections[0].split('\n\n')[0].replace('## ', '').trim();

            // Process each constraint
            constraints = constraintSubsections.slice(1).map(subsection => {
                const lines = subsection.split('\n\n');
                const title = lines[0].replace('### ', '').trim();
                const description = lines[1] ? lines[1].trim() : '';

                // Extract the formula - it's typically enclosed in \[ \]
                const formulaMatch = subsection.match(/\\\[([\s\S]*?)\\\]/);
                const formula = formulaMatch ? formulaMatch[1].trim() : '';

                return {
                    title,
                    description,
                    formula
                };
            });
        }

        // Process Objective Function section
        const objectiveSection = sections.find(section => section.startsWith('## Objective Function'));
        let objective: ObjectiveFunction = {
            title: '',
            description: '',
            formula: ''
        };

        if (objectiveSection) {
            const lines = objectiveSection.split('\n\n');
            objective.title = lines[0].replace('## ', '').trim();

            // The description is typically the text before the formula
            if (lines[1] && !lines[1].includes('\\[')) {
                objective.description = lines[1].trim();
            }

            // Extract the formula
            const formulaMatch = objectiveSection.match(/\\\[([\s\S]*?)\\\]/);
            objective.formula = formulaMatch ? formulaMatch[1].trim() : '';
        }

        // Extract JSON representation
        const jsonMatch = formulation.match(/```json\s*([\s\S]*?)\s*```/);
        const jsonData = jsonMatch ? jsonMatch[1].trim() : '';

        // Extract LaTeX formulation
        const latexMatch = formulation.match(/```latex\s*([\s\S]*?)\s*```/);
        const latexData = latexMatch ? latexMatch[1].trim() : '';

        return {
            title,
            description,
            variables,
            parameters,
            constraints,
            objective,
            jsonData,
            latexData
        };
    } catch (error) {
        console.error('Error formatting formulation:', error);
        return null;
    }
};